import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { App, Skeleton, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button, Panel } from '../../components/ds';
import { renderSectionIcon } from '../../lib/sectionIcons';
import { rolesAdminApi, wikiAdminApi } from '../../lib/wikiApi';
import { accessLevel, applyLevel, blockChanged, nextLevel, type AccessLevel, type MatrixBlock, type MatrixRole } from './access-matrix';

const LEVEL: Record<AccessLevel | 3, { label: string; fg: string; bg: string }> = {
  0: { label: 'Нет', fg: 'var(--fk-text-muted)', bg: 'var(--fk-bg-subtle)' },
  1: { label: 'Просмотр', fg: 'var(--fk-info)', bg: 'var(--fk-info-soft)' },
  2: { label: 'Редакт.', fg: 'var(--fk-orange)', bg: 'var(--fk-orange-soft)' },
  3: { label: 'Публикация', fg: 'var(--fk-success-text)', bg: 'var(--fk-success-soft)' },
};

type Col = MatrixRole & { name: string; source: 'crm' | 'iiko'; canPublish: boolean };

const AUTHOR_PERMS = ['*', 'kb:create', 'kb:edit_any', 'kb:admin'];
const PUBLISH_PERMS = ['*', 'kb:publish', 'kb:admin'];

/**
 * Матрица «раздел базы × роль» (HUB 2.0): клик по ячейке — Нет → Просмотр →
 * Редактирование. Сохраняется в разделы (PATCH /wiki/admin/blocks/:id:
 * visibleToRoles / writeRoles). «Публикация» — право CRM kb:publish, здесь
 * только показывается. Роли и права самих ролей меняются в CRM.
 */
export function AccessMatrix(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ['wiki-admin', 'roles'], queryFn: () => rolesAdminApi.list(), retry: false });
  const audiences = useQuery({ queryKey: ['wiki-admin', 'category-audiences'], queryFn: () => wikiAdminApi.categoryAudiences(), staleTime: 10 * 60_000 });
  const blocks = useQuery({ queryKey: ['wiki-admin', 'blocks'], queryFn: () => wikiAdminApi.listBlocks() });
  const [draft, setDraft] = useState<Record<string, MatrixBlock>>({});

  const cols = useMemo<Col[]>(() => {
    const crm: Col[] = (roles.data ?? [])
      .filter((r) => r.userCount > 0 || r.isProtected)
      .map((r) => ({
        code: r.code,
        name: r.name,
        source: 'crm',
        canAuthor: r.permissions.some((p) => AUTHOR_PERMS.includes(p)),
        canPublish: r.permissions.some((p) => PUBLISH_PERMS.includes(p)),
      }));
    const seen = new Set(crm.map((c) => c.code));
    const iiko: Col[] = (audiences.data ?? [])
      .filter((a) => a.source === 'iiko' && !seen.has(a.code))
      .map((a) => ({ code: a.code, name: a.name, source: 'iiko', canAuthor: false, canPublish: false }));
    return [...crm, ...iiko];
  }, [roles.data, audiences.data]);

  const rows = blocks.data ?? [];
  const cur = (id: string, b: MatrixBlock): MatrixBlock => draft[id] ?? { visibleToRoles: b.visibleToRoles ?? [], writeRoles: b.writeRoles ?? [] };
  const changed = rows.filter((b) => draft[b.id] && blockChanged(draft[b.id], { visibleToRoles: b.visibleToRoles ?? [], writeRoles: b.writeRoles ?? [] }));

  const save = useMutation({
    mutationFn: async () => {
      for (const b of changed) await wikiAdminApi.updateBlock(b.id, draft[b.id]);
      return changed.length;
    },
    onSuccess: (n) => {
      void message.success(`Права сохранены: ${n} ${n === 1 ? 'раздел' : 'разделов'}`);
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['wiki-admin', 'blocks'] });
      void qc.invalidateQueries({ queryKey: ['wiki-admin', 'roles'] });
      void qc.invalidateQueries({ queryKey: ['wiki', 'blocks'] });
    },
    onError: (e: Error) => void message.error(e.message),
  });

  if (roles.isLoading || blocks.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Panel
      title="Доступ к разделам базы"
      sub="нажмите на ячейку, чтобы изменить · обучение и стандарты назначаются отдельно — курсами и траекториями"
      pad={0}
      right={
        <span style={{ display: 'inline-flex', gap: 8 }}>
          {changed.length > 0 && (
            <Button size="sm" variant="text" onClick={() => setDraft({})}>
              Отменить
            </Button>
          )}
          <Button size="sm" variant="primary" disabled={!changed.length} loading={save.isPending} onClick={() => save.mutate()}>
            Сохранить права{changed.length ? ` · ${changed.length}` : ''}
          </Button>
        </span>
      }
    >
      <div className="fk-adm-hint" style={{ padding: '10px 16px', borderBottom: '1px solid var(--fk-row-line)' }}>
        <InfoCircleOutlined style={{ color: 'var(--fk-text-muted)' }} />
        <span style={{ flex: 1, minWidth: 220 }}>
          Пока ни одна роль не ограничена, раздел видят все — и новые роли тоже. Ограничили хотя бы одну — новые роли нужно будет отметить
          вручную.
        </span>
        {([0, 1, 2, 3] as const).map((k) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: LEVEL[k].fg }} />
            {LEVEL[k].label}
          </span>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="fk-adm-matrix">
          <thead>
            <tr>
              <th>Раздел</th>
              {cols.map((c) => (
                <th key={c.code} className="fk-adm-c">
                  {c.name}
                  {c.source === 'iiko' && <div className="fk-adm-note" style={{ fontWeight: 500 }}>iiko</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => {
              const block = cur(b.id, b);
              return (
                <tr key={b.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: b.color || 'var(--fk-indigo)' }}>{renderSectionIcon(b.icon)}</span>
                      <b>{b.name}</b>
                      {draft[b.id] && blockChanged(draft[b.id], { visibleToRoles: b.visibleToRoles ?? [], writeRoles: b.writeRoles ?? [] }) && (
                        <span className="fk-adm-dot" style={{ background: 'var(--fk-orange)' }} title="Изменено" />
                      )}
                    </span>
                  </td>
                  {cols.map((c) => {
                    const lv = accessLevel(block, c);
                    const shown: AccessLevel | 3 = lv === 2 && c.canPublish ? 3 : lv;
                    const meta = LEVEL[shown];
                    return (
                      <td key={c.code} className="fk-adm-c">
                        <Tooltip title={!c.canAuthor && lv === 1 ? 'У роли нет права писать статьи (kb:create в CRM)' : undefined}>
                          <button
                            type="button"
                            onClick={() => setDraft((d) => ({ ...d, [b.id]: applyLevel(block, c, nextLevel(lv, c), cols) }))}
                            style={{
                              minWidth: 92,
                              padding: '4px 10px',
                              borderRadius: 4,
                              border: 'none',
                              cursor: 'pointer',
                              font: 'inherit',
                              fontSize: 12.5,
                              fontWeight: 700,
                              background: meta.bg,
                              color: meta.fg,
                            }}
                          >
                            {meta.label}
                          </button>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
